import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import SettingsMenu from "../features/settings/SettingsMenu.js";
import useApi from "../hooks/useApi.js";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import SettingsIcon from "@mui/icons-material/Settings";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import InstallMobileIcon from "@mui/icons-material/InstallMobile";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import LoginIcon from "@mui/icons-material/Login";

import { useAuth } from "../context/authContext.js";
import Avatar from "@mui/material/Avatar";
import { Link, useNavigate } from "react-router-dom";
import SearchButton from "./SearchButton.js";
import SearchBar from "./SearchBar.js";
import { usePageLocation } from "../routes/AppRoutes.js";
import { useDetailNavigation } from "../hooks/useDetailNavigation.js";
import {
  DialogContent,
  Stack,
  SvgIcon,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import StandardDialog from "./StandardDialog.js";
import CampaignIcon from "@mui/icons-material/Campaign";
import FeedbackIcon from "@mui/icons-material/Feedback";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import LogoSmallDark from "../assets/LogoLargeDark.svg";
import LogoSmallLight from "../assets/LogoLargeLight.svg";
import { ReactComponent as TrackIcon } from "../assets/IconTrack.svg";
import { ReactComponent as CurateIcon } from "../assets/IconCurate.svg";
import { ReactComponent as ExploreIcon } from "../assets/IconExplore.svg";
import { applyStatusWriteToCache } from "../utils/cacheUtils.js";
import { getContentLabel, handleApiError } from "../utils/utils";
import { clearLastTrackPath } from "../utils/lastTrackPath.js";
import { toast } from "react-toastify";
import { useThemeContext } from "../context/themeContext.js";
import HelpDialog from "./HelpAndTips.js";
import SidebarPrompt from "./SidebarPrompt.js";
import { useUnreadNotificationCount } from "../hooks/useCustomQuery.js";
import BetaPill from "./BetaPill.js";
import UserSearch from "../features/explore/UserSearch.js";
import CampaignDialog from "../features/campaign/CampaignDialog.js";
import {
  AUTO_OPEN_DELAY_MS,
  getPromptMessage,
  hasSeenCampaignDialog,
  markCampaignDialogSeen,
  PROMPT_LEAD_IN,
  useCampaignPhase,
} from "../features/campaign/campaign.js";

// ui_state keys backing the two standing sidebar prompts. Both are pre-set to true in
// register_new_user's default_ui_state; a missing key (accounts predating them) also
// reads as "show", so only an explicit false hides a prompt.
const IMPORT_PROMPT_KEY = "show-library-import-prompt";
const INSTALL_PROMPT_KEY = "show-mobile-install-prompt";

function ResponsiveDrawer({
  mobileOpen,
  handleDrawerTransitionEnd,
  handleBackdropClose,
  closeDrawer,
  drawerWidth,
  isFabAnimated,
}) {
  // Custom hooks
  const theme = useTheme();
  const { isTouchDevice } = useThemeContext();
  const { user, updateUser, login } = useAuth();
  const { addStatus, editUiState } = useApi();
  const { data: unreadWhatsNew } = useUnreadNotificationCount("whats_new");
  const queryClient = useQueryClient();
  // The page beneath any open modal — nav highlighting and the dialog-clearing
  // effect below both care about the page, not a detail dialog on top of it.
  const pageLocation = usePageLocation();
  const navigate = useNavigate();
  const { openContent } = useDetailNavigation();
  const isActive = (path) => pageLocation.pathname.startsWith(path);
  const fullscreen = useMediaQuery(theme.breakpoints.down("sm"));
  // Local mirrors of the two standing prompts' dismissal, so closing one hides it
  // instantly rather than waiting on the round trip that persists it.
  const [importPromptDismissed, setImportPromptDismissed] = useState(false);
  const [installPromptDismissed, setInstallPromptDismissed] = useState(false);
  const [showCampaignPrompt, setShowCampaignPrompt] = useState(true);
  const [showCampaignDialog, setShowCampaignDialog] = useState(false);
  const { phase: campaignPhase, daysUntil: campaignDaysUntil } =
    useCampaignPhase();

  // States
  const [settingsMenuAnchorEl, setSettingsMenuAnchorEl] = useState(null);
  const [searchBarType, setSearchBarType] = useState(null);
  const [showHelp, setShowHelp] = useState("none");

  // The drawer is part of the persistent layout (outside the router Outlet), so its
  // search dialog doesn't unmount on navigation the way page-owned ones do. Close it
  // on path change to avoid leaving it stranded on top of the new page (e.g. tapping
  // "edit" in the watch-provider popover routes to /settings/services). Content detail
  // is no longer local state here, so this no longer needs to clear it too.
  useEffect(() => {
    setSearchBarType(null);
  }, [pageLocation.pathname]);

  // Auto-opens the campaign dialog once, a beat after load so it doesn't land mid
  // first-paint. The "already seen" flag lives in localStorage rather than the user's
  // ui_state (see features/campaign/campaign.js), so this works for signed-out visitors
  // too and needs no backend involvement. It's written when the dialog opens rather than
  // when it closes, so a refresh can't re-trigger it.
  useEffect(() => {
    if (campaignPhase === "over" || hasSeenCampaignDialog()) return;

    const timer = setTimeout(() => {
      setShowCampaignDialog(true);
      markCampaignDialogSeen();
    }, AUTO_OPEN_DELAY_MS);

    return () => clearTimeout(timer);
  }, [campaignPhase]);

  // Permanently dismisses one of the standing sidebar prompts: hides it immediately, then
  // persists it to the user's ui_state so it stays gone on future visits and other
  // devices. Signed-out visitors have no ui_state to write to, so their dismissal lasts
  // the session only. The in-memory user is patched alongside the request so a re-mount
  // doesn't flash the prompt back before the next re-auth.
  const { mutate: persistPromptDismissal } = useMutation({
    mutationFn: editUiState,
  });

  const dismissPrompt = (key, setDismissed) => {
    setDismissed(true);
    if (!user) return;

    persistPromptDismissal({ key, value: false });
    updateUser({ ...user, uiState: { ...user.uiState, [key]: false } });
  };

  // Handlers
  const handleSettingsMenuOpen = (event) => {
    if (settingsMenuAnchorEl !== event.currentTarget) {
      setSettingsMenuAnchorEl(event.currentTarget);
    }
  };

  const addStatusMutation = useMutation({
    mutationFn: addStatus,
    onSuccess: (data, variables) => {
      applyStatusWriteToCache({
        queryClient,
        data,
        requestedContentId: variables.contentId,
        oldStatus: null,
        contentType: variables.contentType,
        username: user.username,
      });
      // The search bar stays open for further adds, so this toast is the only
      // confirmation. Its message names the content the write actually landed
      // on — for a multi-season series that's the resolved season (see
      // resolve_tv_action_content), which may differ from what was tapped.
      toast.success(data.message);
    },
    onError: (error, variables) => {
      // A multi-season series can't always tell which season a quick
      // queue/start should target (see resolve_tv_action_content) — that
      // refusal arrives as this specific error code, so it gets a tappable
      // toast that closes search and opens the show (to pick a season)
      // instead of the generic error toast.
      if (error.response?.data?.code === "ambiguous_season_target") {
        toast.error(error.response.data.message, {
          onClick: () => {
            setSearchBarType(null);
            openContent({
              contentId: variables.contentId,
              contentType: variables.contentType,
            });
          },
          autoClose: false,
        });
      } else {
        handleApiError(error);
      }
    },
  });

  // Leaves the search bar open so several items can be added in one go.
  const handleQuickAdd = (contentId, status, contentType) => {
    addStatusMutation.mutate({
      contentId,
      contentType,
      status,
      createJournalEntry: true,
    });
  };

  const handleSearchSelect = (contentId, contentType) => {
    setSearchBarType(null);
    openContent({ contentId, contentType });
  };

  const handleSearchButtonClick = (type) => {
    setSearchBarType(type);
  };

  const handleImportClick = () => {
    navigate("/import");
    closeDrawer();
  };

  // A missing key reads as "show", so only an explicit false hides these. The install
  // prompt is offered to signed-out visitors too, hence the `!user ||` branch; the import
  // prompt is meaningless without an account to import into.
  const showImportPrompt =
    !importPromptDismissed &&
    Boolean(user) &&
    user.uiState?.[IMPORT_PROMPT_KEY] !== false;

  const showInstallPrompt =
    !installPromptDismissed &&
    isTouchDevice &&
    (!user || user.uiState?.[INSTALL_PROMPT_KEY] !== false);

  const trianglePointerStyle = {
    position: "absolute",
    right: -10,
    top: "50%",
    transform: "translateY(-50%) rotate(45deg)",
    width: 20,
    height: 20,
    bgcolor: "background.paper",
    zIndex: 0,
  };

  const drawer = (
    <Box display="flex" flexDirection="column" height="100%">
      <Box flexGrow={1}>
        <List>
          <ListItem disablePadding sx={{ pt: 2 }}>
            <ListItemButton
              component={Link}
              to="/?redirect=false"
              sx={{ px: 0 }}
              onClick={closeDrawer}
            >
              <ListItemIcon>
                <img
                  src={
                    theme.palette.mode === "dark"
                      ? LogoSmallDark
                      : LogoSmallLight
                  }
                  alt="Curate Logo"
                  style={{ width: 165, paddingLeft: 16 }}
                />
              </ListItemIcon>
            </ListItemButton>
          </ListItem>

          {user ? (
            <ListItem key="profile" disablePadding sx={{ pt: 2 }}>
              <ListItemButton
                component={Link}
                to={`/u/${user?.username}`}
                onClick={closeDrawer}
              >
                <ListItemIcon>
                  <Avatar
                    alt={user?.givenName}
                    src={user?.avatar_url}
                    imgProps={{ referrerPolicy: "no-referrer" }}
                    loading="lazy"
                    sx={{ display: "flex", bgcolor: "tertiary.main" }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={user?.givenName}
                  primaryTypographyProps={{
                    fontWeight: isActive(`/u/${user?.username}`)
                      ? "bold"
                      : "undefined",
                    color: (theme) =>
                      isActive(`/u/${user?.username}`)
                        ? theme.palette.primary.main
                        : "undefined",
                  }}
                />
                {isActive(`/u/${user?.username}`) && !mobileOpen && (
                  <Box component="span" sx={trianglePointerStyle} />
                )}
              </ListItemButton>
            </ListItem>
          ) : (
            <ListItem key="profile" disablePadding sx={{ pt: 2 }}>
              <ListItemButton onClick={login}>
                <ListItemIcon>
                  <LoginIcon />
                </ListItemIcon>
                <ListItemText
                  primary={
                    "Log in or create account to access full features (it's free!)"
                  }
                  primaryTypographyProps={{ variant: "body2" }}
                />
              </ListItemButton>
            </ListItem>
          )}
        </List>

        <List>
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to={`/track`}
              onClick={() => {
                // /track resumes where you left your library — unless you're
                // already there, when tapping Track resets it, as tapping the
                // current tab of an app does
                if (
                  user &&
                  pageLocation.pathname.startsWith(`/track/u/${user.username}/`)
                ) {
                  clearLastTrackPath(user.username);
                }
                closeDrawer();
              }}
            >
              <ListItemIcon>
                <SvgIcon
                  sx={{
                    color: isActive(`/track`) ? "primary.main" : undefined,
                  }}
                >
                  <TrackIcon />
                </SvgIcon>
              </ListItemIcon>
              <ListItemText
                primary="Track"
                primaryTypographyProps={{
                  fontWeight: isActive(`/track`) ? "bold" : "undefined",
                  color: (theme) =>
                    isActive(`/track`)
                      ? theme.palette.primary.main
                      : "undefined",
                }}
              />
              {isActive(`/track`) && !mobileOpen && (
                <Box component="span" sx={trianglePointerStyle} />
              )}
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to={`/curate`}
              onClick={closeDrawer}
              disabled={!user}
            >
              <ListItemIcon>
                <SvgIcon
                  sx={{
                    color: isActive(`/curate`) ? "primary.main" : undefined,
                  }}
                >
                  <CurateIcon />
                </SvgIcon>
              </ListItemIcon>
              <ListItemText
                primary="Curate"
                primaryTypographyProps={{
                  fontWeight: isActive(`/curate`) ? "bold" : "undefined",
                  color: (theme) =>
                    isActive(`/curate`)
                      ? theme.palette.primary.main
                      : "undefined",
                }}
              />
              {isActive(`/curate`) && !mobileOpen && (
                <Box component="span" sx={trianglePointerStyle} />
              )}
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to={"/explore"}
              onClick={closeDrawer}
            >
              <ListItemIcon>
                <SvgIcon
                  sx={{
                    color: isActive("/explore") ? "primary.main" : undefined,
                  }}
                >
                  <ExploreIcon />
                </SvgIcon>
              </ListItemIcon>
              <ListItemText
                primary=<Stack direction="row" spacing={1}>
                  <Typography
                    sx={{
                      fontWeight: isActive("/explore") ? "bold" : "undefined",
                    }}
                  >
                    Explore
                  </Typography>
                  <BetaPill />
                </Stack>
                primaryTypographyProps={{
                  color: (theme) =>
                    isActive("/explore")
                      ? theme.palette.primary.main
                      : "undefined",
                }}
              />
              {isActive("/explore") && !mobileOpen && (
                <Box component="span" sx={trianglePointerStyle} />
              )}
            </ListItemButton>
          </ListItem>
        </List>

        {/* Campaign prompt — only while a campaign is running. Its dismissal is
        session-only by design: a campaign is short-lived, so it isn't a standing
        preference worth persisting the way the two prompts below are. */}
        {campaignPhase !== "over" && showCampaignPrompt && (
          <SidebarPrompt
            icon={<CampaignOutlinedIcon />}
            message={
              <Box component="span" sx={{ fontWeight: "bold" }}>
                <Box component="span" sx={{ color: "tertiary.main" }}>
                  {PROMPT_LEAD_IN}
                </Box>{" "}
                {getPromptMessage(campaignPhase, campaignDaysUntil)}
              </Box>
            }
            onClose={() => setShowCampaignPrompt(false)}
            onClick={() => setShowCampaignDialog(true)}
          />
        )}
      </Box>

      {showInstallPrompt && (
        <SidebarPrompt
          icon={<InstallMobileIcon />}
          message={"Install mobile app"}
          onClose={() =>
            dismissPrompt(INSTALL_PROMPT_KEY, setInstallPromptDismissed)
          }
          onClick={() => setShowHelp("installation")}
        />
      )}

      {showImportPrompt && (
        <SidebarPrompt
          icon={<DriveFileMoveIcon />}
          message={"Import existing library"}
          onClose={() =>
            dismissPrompt(IMPORT_PROMPT_KEY, setImportPromptDismissed)
          }
          onClick={handleImportClick}
        />
      )}

      <List sx={{ mt: 1 }}>
        <ListItem disablePadding>
          <ListItemButton
            component={Link}
            to="/whats-new"
            onClick={closeDrawer}
          >
            <ListItemIcon>
              <CampaignIcon />
            </ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <span>What's New</span>
                  {!!unreadWhatsNew?.count && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        flexShrink: 0,
                      }}
                    />
                  )}
                </Box>
              }
              primaryTypographyProps={{
                fontWeight: isActive("/whats-new") ? "bold" : undefined,
                color: (theme) =>
                  isActive("/whats-new")
                    ? theme.palette.primary.main
                    : undefined,
              }}
            />
            {isActive("/whats-new") && !mobileOpen && (
              <Box component="span" sx={trianglePointerStyle} />
            )}
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={() => setShowHelp("all")}>
            <ListItemIcon>
              <TipsAndUpdatesIcon />
            </ListItemIcon>
            <ListItemText primary="Help & Tips" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            component="a"
            href="https://app.youform.com/forms/fghdoj5a"
            target="_blank"
            rel="noopener noreferrer"
          >
            <ListItemIcon>
              <FeedbackIcon />
            </ListItemIcon>
            <ListItemText primary="Feedback & Support" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={handleSettingsMenuOpen}>
            <ListItemIcon>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary="Settings" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="side bar"
      >
        {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onTransitionEnd={handleDrawerTransitionEnd}
          onClose={handleBackdropClose}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              borderRight: 0,
              backgroundImage: "none",
              backgroundColor: (theme) => theme.palette.secondary.main,
              // Starts below the safe-area top inset and the campaign banner (both 0px
              // when not applicable — see --top-offset in index.js). The drawer is a
              // modal at a higher z-index than the banner, so without this it opens over
              // the top of it (and, on notched devices, under the status bar).
              top: "var(--top-offset, 0px)",
              height: "calc(100% - var(--top-offset, 0px))",
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              overflowX: "hidden",
              borderRight: 0,
              backgroundImage: "none",
              backgroundColor: (theme) => theme.palette.secondary.main,
              // Sits below the safe-area top inset and the campaign banner (both 0px when
              // not applicable — see --top-offset in index.js) so neither is covered by
              // the drawer's left 240px.
              top: "var(--top-offset, 0px)",
              height: "calc(100% - var(--top-offset, 0px))",
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <SettingsMenu
        anchorElement={settingsMenuAnchorEl}
        open={Boolean(settingsMenuAnchorEl)}
        onClose={() => {
          setSettingsMenuAnchorEl(null);
          closeDrawer();
        }}
      />
      <SearchButton
        onSelect={handleSearchButtonClick}
        isFabAnimated={isFabAnimated}
      />
      <StandardDialog
        open={!!searchBarType}
        onClose={() => setSearchBarType(null)}
        maxWidth="sm"
        topOffset="10vh"
        disableRestoreFocus
      >
        <DialogContent
          sx={{
            border: "none",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {searchBarType === "profile" ? (
            <UserSearch
              onClose={() => setSearchBarType(null)}
              fullscreen={fullscreen}
            />
          ) : (
            <SearchBar
              contentType={searchBarType}
              onQuickAdd={handleQuickAdd}
              onSelect={handleSearchSelect}
              fullscreen={fullscreen}
              onClose={() => setSearchBarType(null)}
              title={
                searchBarType
                  ? `Search ${getContentLabel({
                      type: searchBarType,
                      lowercase: true,
                      plural: true,
                    })}...`
                  : ""
              }
            />
          )}
        </DialogContent>
      </StandardDialog>

      <HelpDialog
        open={showHelp !== "none"}
        onClose={() => setShowHelp("none")}
        mode={showHelp}
      />

      {campaignPhase !== "over" && (
        <CampaignDialog
          open={showCampaignDialog}
          onClose={() => setShowCampaignDialog(false)}
        />
      )}
    </>
  );
}

export default ResponsiveDrawer;
